import { createStore, type Store } from "@tanstack/store"
import { retrieveLaunchParams } from "@telegram-apps/bridge"

type TelegramStoreState = {
	status: "idle" | "ready"
	telegramUserId: string | null
	telegramUsername?: string
	source: "unknown" | "launch-params" | "query" | "manual"
}

const initialState: TelegramStoreState = {
	status: "idle",
	telegramUserId: null,
	source: "unknown",
}

export type TelegramStore = Store<TelegramStoreState>

export const telegramStore: TelegramStore = createStore(initialState)

const TELEGRAM_ID_QUERY_KEYS = ["tg_user_id", "tgUserId"] as const

type QuerySearch =
	| string
	| URLSearchParams
	| Record<string, unknown>
	| undefined

function normalizeId(id: string | number | null | undefined): string | null {
	if (id == null) {
		return null
	}
	const value = String(id).trim()
	if (value.length === 0) {
		return null
	}

	// Telegram ID должен быть числовым
	if (!/^\d+$/.test(value)) {
		console.warn("[TelegramStore] Invalid Telegram ID format:", value)
		return null
	}

	return value
}

function setTelegramUserId(
	id: string | number | null,
	source: TelegramStoreState["source"] = "manual",
	username?: string,
) {
	const normalized = normalizeId(id)
	if (!normalized) {
		return
	}

	// Защита от перезаписи: не перезаписываем если уже установлен (write-once паттерн)
	if (telegramStore.state.telegramUserId) {
		console.warn(
			"[TelegramStore] Already initialized with ID:",
			telegramStore.state.telegramUserId,
			"Ignoring new ID:",
			normalized,
		)
		return
	}

	const next: TelegramStoreState = {
		status: "ready",
		telegramUserId: normalized,
		source,
	}
	if (username !== undefined) next.telegramUsername = username
	telegramStore.setState(() => next)
}

function readTelegramIdFromSearch(search: QuerySearch): string | null {
	if (!search) {
		return null
	}

	if (typeof search === "string") {
		const params = new URLSearchParams(search)
		for (const key of TELEGRAM_ID_QUERY_KEYS) {
			const value = params.get(key)
			if (value) {
				return normalizeId(value)
			}
		}
		return null
	}

	if (search instanceof URLSearchParams) {
		for (const key of TELEGRAM_ID_QUERY_KEYS) {
			const value = search.get(key)
			if (value) {
				return normalizeId(value)
			}
		}
		return null
	}

	for (const key of TELEGRAM_ID_QUERY_KEYS) {
		const raw = search[key]
		if (typeof raw === "string") {
			return normalizeId(raw)
		}
		if (Array.isArray(raw)) {
			const candidate = raw.find((item) => typeof item === "string")
			if (candidate) {
				return normalizeId(candidate)
			}
		}
	}

	return null
}

function updateTelegramIdFromSearch(
	search: QuerySearch,
	source: TelegramStoreState["source"] = "query",
) {
	const id = readTelegramIdFromSearch(search)
	if (id) {
		setTelegramUserId(id, source)
	}
	return id
}

export function bootstrapTelegramStore() {
	if (typeof window === "undefined") {
		return telegramStore.state
	}

	// Method 1: Try SDK retrieveLaunchParams (official method)
	try {
		const params = retrieveLaunchParams()
		const user = params.tgWebAppData?.user
		const launchUserId = normalizeId(user?.id)

		if (launchUserId) {
			console.log("[Telegram] Init: SDK retrieveLaunchParams ✓")
			setTelegramUserId(launchUserId, "launch-params", user?.username)
			return telegramStore.state
		}
	} catch (error) {
		let reason = "Unknown error"
		if (error instanceof Error) reason = error.message
		console.debug("[Telegram] Init: SDK retrieveLaunchParams failed -", reason)
	}

	// Method 2: Try URL query parameters (fallback)
	if (!telegramStore.state.telegramUserId) {
		const queryId = updateTelegramIdFromSearch(window.location.search, "query")
		if (queryId) {
			console.log("[Telegram] Init: URL query parameters ✓")
			return telegramStore.state
		}
		console.debug(
			"[Telegram] Init: URL query parameters failed - no params found",
		)
	}

	// All methods failed
	if (!telegramStore.state.telegramUserId) {
		console.warn(
			"[Telegram] Init: All methods failed - app likely running outside Telegram environment",
		)
	}

	return telegramStore.state
}

export function resolveTelegramUserId(
	store: TelegramStore,
	locationSearch?: Record<string, unknown>,
): string | null {
	let id = store.state.telegramUserId
	if (id) return id

	bootstrapTelegramStore()
	id = store.state.telegramUserId
	if (id) return id

	if (locationSearch) {
		updateTelegramIdFromSearch(locationSearch)
		id = store.state.telegramUserId
	}

	return id
}

export function getTelegramUserId() {
	return telegramStore.state.telegramUserId
}

export function getTelegramUsername() {
	return telegramStore.state.telegramUsername
}

export function clearTelegramData() {
	telegramStore.setState(() => initialState)
}

/**
 * Мобильные платформы Telegram, на которых приложение работает корректно.
 * @see https://docs.telegram-mini-apps.com/platform/launch-parameters
 */
const MOBILE_PLATFORMS: readonly string[] = ["android", "android_x", "ios"]

export function isDesktopPlatform(): boolean {
	if (typeof window === "undefined") {
		return false
	}

	try {
		const params = retrieveLaunchParams()
		const platform = params.tgWebAppPlatform
		return !MOBILE_PLATFORMS.includes(platform)
	} catch {
		// Вне Telegram - считаем desktop (браузер)
		return true
	}
}
